"use client";

import { useCallback, useMemo, useState } from "react";
import { ArrowLeft, HardDrive } from "lucide-react";
import { FaChevronLeft } from "react-icons/fa";
import { Button } from "@/shadcn-components/ui/button";
import { useAccountsStore } from "@/lib/accounts/store";
import type { TradingAccount } from "@/lib/accounts/types";
import { useStorageInventory } from "@/lib/storage/useStorageInventory";
import { findAccountInventory } from "@/lib/storage/inventory";
import type { AccountCacheTypeId, AppCacheTypeId } from "@/lib/storage/inventory";
import { formatBytes } from "@/lib/storage/calculate";
import {
  cleanAppCache,
  cleanAllCaches,
  clearAccountCache,
  clearAccountCacheType,
  clearAccountSymbolCache,
} from "@/lib/storage/clear";
import { accountPrefix, clearIdbOrphanData } from "@/lib/storage/idb-ops";
import { invalidateStorageInventorySnapshot } from "@/lib/storage/inventory-snapshot";
import { ClearCacheDialog } from "./ClearCacheDialog";
import { StorageHomeView } from "./StorageHomeView";
import { AppCacheDetailView } from "./AppCacheDetailView";
import { AccountCacheDetailView } from "./AccountCacheDetailView";

type View = "home" | "app" | "account";

type ClearTarget =
  | { kind: "app-all" }
  | { kind: "app-type"; type: AppCacheTypeId; label: string; bytes: number }
  | { kind: "all" }
  | { kind: "account"; accountId: string; label: string; bytes: number }
  | { kind: "account-type"; accountId: string; type: AccountCacheTypeId; label: string; bytes: number }
  | { kind: "symbol"; accountId: string; symbol: string; bytes: number }
  | { kind: "orphan"; bytes: number };

interface StorageManagementProps {
  variant: "mobile" | "desktop";
  onBack: () => void;
}

export function StorageManagement({ variant, onBack }: StorageManagementProps) {
  const accounts = useAccountsStore((s) => s.accounts);
  const activeAccountId = useAccountsStore((s) => s.activeAccountId);
  const {
    inventory,
    loading,
    refreshing,
    error,
    lastScannedAt,
    refresh,
    invalidateAndRefresh,
  } = useStorageInventory();
  const [view, setView] = useState<View>("home");
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [clearTarget, setClearTarget] = useState<ClearTarget | null>(null);
  const [confirming, setConfirming] = useState(false);

  const selectedAccount = useMemo(() => {
    if (!inventory || !selectedAccountId) return undefined;
    return findAccountInventory(inventory, selectedAccountId);
  }, [inventory, selectedAccountId]);

  const openClear = useCallback((target: ClearTarget) => {
    setClearTarget(target);
  }, []);

  const closeClear = useCallback(() => {
    if (!confirming) setClearTarget(null);
  }, [confirming]);

  const handleConfirmClear = useCallback(async () => {
    if (!clearTarget) return;
    const kind = clearTarget.kind;
    const clearedAccountId =
      kind === "account" ? clearTarget.accountId : null;
    setConfirming(true);
    try {
      switch (kind) {
        case "app-all":
          cleanAppCache();
          break;
        case "app-type":
          cleanAppCache(
            clearTarget.kind === "app-type" ? [clearTarget.type] : undefined,
          );
          break;
        case "all":
          await cleanAllCaches();
          return;
        case "account":
          await clearAccountCache(clearTarget.accountId);
          break;
        case "account-type":
          await clearAccountCacheType(
            clearTarget.accountId,
            clearTarget.type,
          );
          break;
        case "symbol":
          await clearAccountSymbolCache(
            clearTarget.accountId,
            clearTarget.symbol,
          );
          break;
        case "orphan": {
          const prefixes = accounts.map((a: TradingAccount) =>
            accountPrefix(a.companyName, a.serverName),
          );
          await clearIdbOrphanData(prefixes);
          invalidateStorageInventorySnapshot();
          break;
        }
      }
      setClearTarget(null);
      await refresh({ background: true });
      if (
        kind === "account" &&
        clearedAccountId &&
        selectedAccountId === clearedAccountId
      ) {
        setView("home");
        setSelectedAccountId(null);
      }
    } finally {
      setConfirming(false);
    }
  }, [clearTarget, refresh, selectedAccountId]);

  const clearDialogMeta = useMemo(() => {
    if (!clearTarget) return null;
    const baseDesc =
      "Cached data on this device will be removed. It can be downloaded again when you use the app.";
    switch (clearTarget.kind) {
      case "app-all":
        return {
          title: "Clear app cache",
          description: baseDesc,
          sizeLabel: inventory
            ? formatBytes(inventory.app.totalBytes)
            : undefined,
        };
      case "app-type":
        return {
          title: `Clear ${clearTarget.label}`,
          description: baseDesc,
          sizeLabel: formatBytes(clearTarget.bytes),
        };
      case "all":
        return {
          title: "Clear all caches",
          description:
            "All app and account caches on this device will be removed. The page will reload.",
          sizeLabel: inventory
            ? formatBytes(inventory.totalBytes)
            : undefined,
        };
      case "account":
        return {
          title: "Clear account cache",
          description: baseDesc,
          sizeLabel: formatBytes(clearTarget.bytes),
        };
      case "account-type":
        return {
          title: `Clear ${clearTarget.label}`,
          description: baseDesc,
          sizeLabel: formatBytes(clearTarget.bytes),
        };
      case "symbol":
        return {
          title: `Clear ${clearTarget.symbol}`,
          description: `Remove cached chart data for ${clearTarget.symbol}.`,
          sizeLabel: formatBytes(clearTarget.bytes),
        };
      case "orphan":
        return {
          title: "Clear other cached data",
          description:
            "Remove market data cached for accounts that are no longer configured.",
          sizeLabel: formatBytes(clearTarget.bytes),
        };
    }
  }, [clearTarget, inventory]);

  const handleBack = () => {
    if (view === "home") {
      onBack();
    } else {
      setView("home");
      setSelectedAccountId(null);
    }
  };

  const title =
    view === "home"
      ? "Storage"
      : view === "app"
        ? "App cache"
        : selectedAccount?.label ?? "Account cache";

  const isDesktop = variant === "desktop";

  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      <div
        className={
          isDesktop
            ? "px-6 py-6 border-b border-border shrink-0"
            : "flex items-center px-4 py-3 border-b shrink-0"
        }
      >
        {isDesktop ? (
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                <HardDrive className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">{title}</h2>
                <p className="text-sm text-muted-foreground">
                  Manage cached data on this device
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="mr-4 p-0 h-auto flex items-center gap-2 text-primary hover:bg-transparent"
            >
              <FaChevronLeft className="h-4 w-4" />
              <span className="text-base">
                {view === "home" ? "Settings" : "Storage"}
              </span>
            </Button>
            <h1 className="text-lg font-medium flex-1 text-center mr-16">
              {title}
            </h1>
          </>
        )}
      </div>

      <div
        className={`flex-1 overflow-y-auto ${isDesktop ? "px-6 py-4" : "p-4"}`}
      >
        {error && (
          <p className="text-sm text-destructive mb-4">{error}</p>
        )}
        {loading && !inventory ? (
          <p className="text-sm text-muted-foreground">Loading storage…</p>
        ) : inventory ? (
          <>
            {view === "home" && (
              <StorageHomeView
                inventory={inventory}
                activeAccountId={activeAccountId}
                loading={loading}
                refreshing={refreshing}
                lastScannedAt={lastScannedAt}
                onRefresh={() => void invalidateAndRefresh()}
                onOpenApp={() => setView("app")}
                onOpenAccount={(id) => {
                  setSelectedAccountId(id);
                  setView("account");
                }}
                onClearApp={() => openClear({ kind: "app-all" })}
                onClearAll={() => openClear({ kind: "all" })}
                onClearOrphan={() =>
                  openClear({
                    kind: "orphan",
                    bytes: inventory.orphan?.bytes ?? 0,
                  })
                }
              />
            )}
            {view === "app" && (
              <AppCacheDetailView
                app={inventory.app}
                onClearType={(type) => {
                  const row = inventory.app.types.find((t) => t.id === type);
                  openClear({
                    kind: "app-type",
                    type,
                    label: row?.label ?? type,
                    bytes: row?.bytes ?? 0,
                  });
                }}
                onClearAll={() => openClear({ kind: "app-all" })}
              />
            )}
            {view === "account" && selectedAccount && (
              <AccountCacheDetailView
                account={selectedAccount}
                onClearType={(type) => {
                  const row = selectedAccount.types.find((t) => t.id === type);
                  openClear({
                    kind: "account-type",
                    accountId: selectedAccount.accountId,
                    type,
                    label: row?.label ?? type,
                    bytes: row?.bytes ?? 0,
                  });
                }}
                onClearSymbol={(symbol) => {
                  const market = selectedAccount.types.find(
                    (t) => t.id === "marketData",
                  );
                  const item = market?.items?.find((i) => i.id === symbol);
                  openClear({
                    kind: "symbol",
                    accountId: selectedAccount.accountId,
                    symbol,
                    bytes: item?.bytes ?? 0,
                  });
                }}
                onClearAccount={() =>
                  openClear({
                    kind: "account",
                    accountId: selectedAccount.accountId,
                    label: selectedAccount.label,
                    bytes: selectedAccount.totalBytes,
                  })
                }
              />
            )}
          </>
        ) : null}
      </div>

      {clearDialogMeta && (
        <ClearCacheDialog
          open={clearTarget != null}
          onOpenChange={(open) => !open && closeClear()}
          title={clearDialogMeta.title}
          description={clearDialogMeta.description}
          sizeLabel={clearDialogMeta.sizeLabel}
          onConfirm={() => void handleConfirmClear()}
          confirming={confirming}
        />
      )}
    </div>
  );
}
